import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLogoGalleries } from '@/hooks/useLogoGalleries';
import { useClientLogos } from '@/hooks/useClientLogos';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Image } from 'lucide-react';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Event } from '@/types/events';

interface EventClientLogosInfoProps {
  event: Event;
  brandId: string;
}

export function EventClientLogosInfo({ event, brandId }: EventClientLogosInfoProps) {
  const { galleries, isLoading: galleriesLoading } = useLogoGalleries(brandId);
  const [selectedGalleryId, setSelectedGalleryId] = useState<string>('');
  const { logos, isLoading: logosLoading } = useClientLogos(brandId, selectedGalleryId || undefined);
  const { toast } = useToast();
  const [selectedLogoIds, setSelectedLogoIds] = useState<string[]>(event.client_logo_ids || []);
  const [isSaving, setIsSaving] = useState(false);

  const handleLogoToggle = (logoId: string, checked: boolean) => {
    setSelectedLogoIds((prev) => (checked ? [...prev, logoId] : prev.filter((id) => id !== logoId)));
  };

  const handleSelectAll = () => {
    const currentGalleryLogoIds = logos.map((logo) => logo.id);
    const allSelected = currentGalleryLogoIds.every((id) => selectedLogoIds.includes(id));
    if (allSelected) {
      setSelectedLogoIds((prev) => prev.filter((id) => !currentGalleryLogoIds.includes(id)));
    } else {
      const newIds = [...selectedLogoIds];
      currentGalleryLogoIds.forEach((id) => {
        if (!newIds.includes(id)) newIds.push(id);
      });
      setSelectedLogoIds(newIds);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase.from('os_events').update({ client_logo_ids: selectedLogoIds }).eq('id', event.id);
      if (error) throw error;
      toast({ title: 'Success', description: 'Client logos updated successfully' });
    } catch (err: unknown) {
      toast({ title: 'Error', description: 'Failed to update client logos', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  if (galleriesLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  const currentGalleryLogoIds = logos.map((logo) => logo.id);
  const allCurrentSelected = currentGalleryLogoIds.length > 0 && currentGalleryLogoIds.every((id) => selectedLogoIds.includes(id));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Client Logos</CardTitle>
        <p className="text-sm text-muted-foreground">Select logos to display on this event&apos;s public page</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {galleries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Image className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <p className="text-sm text-muted-foreground">No logo galleries found. Create galleries in the Client Logos section first.</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Select Gallery</label>
                <Select value={selectedGalleryId} onValueChange={setSelectedGalleryId}>
                  <SelectTrigger className="w-full bg-background">
                    <SelectValue placeholder="Choose a gallery" />
                  </SelectTrigger>
                  <SelectContent className="bg-background z-50">
                    {galleries.map((gallery) => (
                      <SelectItem key={gallery.id} value={gallery.id}>
                        {gallery.name}
                        {!gallery.is_published && <span className="ml-2 text-xs text-amber-600">(Draft)</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedGalleryId && (
                <>
                  {logosLoading ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-32 w-full" />
                      ))}
                    </div>
                  ) : logos.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">No logos in this gallery</div>
                  ) : (
                    <>
                      <div className="flex justify-between items-center">
                        <Button variant="outline" size="sm" onClick={handleSelectAll} type="button">
                          {allCurrentSelected ? 'Deselect All' : 'Select All'}
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          {selectedLogoIds.length} logo{selectedLogoIds.length !== 1 ? 's' : ''} selected
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-[600px] overflow-y-auto">
                        {logos.map((logo) => {
                          const isSelected = selectedLogoIds.includes(logo.id);
                          return (
                            <div
                              key={logo.id}
                              className={`relative border rounded-lg p-3 cursor-pointer transition-all hover:shadow-md ${
                                isSelected ? 'ring-2 ring-primary border-primary' : 'border-border'
                              }`}
                              onClick={() => handleLogoToggle(logo.id, !isSelected)}
                            >
                              <div className="absolute top-2 left-2 z-10">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) => handleLogoToggle(logo.id, checked as boolean)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className="flex flex-col items-center gap-2">
                                <div className="w-full h-24 flex items-center justify-center bg-muted rounded">
                                  <img
                                    src={logo.logo_url}
                                    alt={logo.alt_text || logo.name}
                                    className="max-w-full max-h-full object-contain"
                                  />
                                </div>
                                <p className="text-xs text-center font-medium truncate w-full">{logo.name}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="flex justify-end pt-4 border-t">
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Client Logos'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
